import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faArrowCircleLeft, faSpinner, faHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import instance from '../apiInstance';
import StoryCartDock, { AddToCartButton } from '../components/StoryCartDock';
import { hydrateStoryCartPrices, titleFromImageUrl, readStoryCart, brandFromUrl } from '../storyCart';
import PromoGenieModal from '../components/PromoGenieModal';
import { trackPiece } from '../trackerBus';
import { openTelegramVendas } from '../openVendas';
import { isAdminUser } from '../adminEmails';
const AUTO_ADVANCE_MS = 5000;
const FAST_SLOTS = 10;
const BG_SLOTS = 4;

const pool = new Map();
let fastQueue = [];
let bgQueue = [];
let fastActive = 0;
let bgActive = 0;

function pump() {
    while (fastActive < FAST_SLOTS && fastQueue.length > 0) {
        startDownload(fastQueue.shift(), true);
    }
    while (bgActive < BG_SLOTS && bgQueue.length > 0) {
        startDownload(bgQueue.shift(), false);
    }
}

function startDownload(item, isFast) {
    const { url, entry } = item;
    if (entry.loaded || entry.downloading) { pump(); return; }
    entry.downloading = true;
    if (isFast) fastActive++; else bgActive++;
    const img = new Image();
    img.onload = () => {
        entry.loaded = true;
        entry.downloading = false;
        if (isFast) fastActive--; else bgActive--;
        pump();
    };
    img.onerror = () => {
        entry.downloading = false;
        if (isFast) fastActive--; else bgActive--;
        pump();
    };
    img.src = url;
    entry.img = img;
}

function enqueue(url, priority) {
    let entry = pool.get(url);
    if (!entry) {
        entry = { loaded: false, downloading: false };
        pool.set(url, entry);
    }
    if (entry.loaded || entry.downloading) return;

    const item = { url, entry };
    if (priority) {
        fastQueue.push(item);
    } else {
        bgQueue.push(item);
    }
    pump();
}

function boost(url) {
    const entry = pool.get(url);
    if (!entry || entry.loaded || entry.downloading) return;
    const idx = bgQueue.findIndex(q => q.url === url);
    if (idx >= 0) {
        const [item] = bgQueue.splice(idx, 1);
        fastQueue.unshift(item);
    } else {
        const fIdx = fastQueue.findIndex(q => q.url === url);
        if (fIdx > 0) {
            const [item] = fastQueue.splice(fIdx, 1);
            fastQueue.unshift(item);
        }
    }
    pump();
}

function isReady(url) {
    return pool.get(url)?.loaded === true;
}

const Dashboard = () => {
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);
    const [idx, setIdx] = useState(0);
    const [urls, setUrls] = useState([]);
    const [prices, setPrices] = useState({});
    const [fetched, setFetched] = useState(false);
    const [visible, setVisible] = useState(false);
    const [showTip, setShowTip] = useState(true);
    const [genieInfo, setGenieInfo] = useState(null); // {matchCount, strongMatchCount, injectedIndices, wish}
    const [showGenieBadge, setShowGenieBadge] = useState(false);
    const [wishExpired, setWishExpired] = useState(false);
    const [genieOpen, setGenieOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const timerRef = useRef(null);
    const pollRef = useRef(null);
    const imgRef = useRef(null);
    const idxRef = useRef(0);

    const [favoritedUrls, setFavoritedUrls] = useState(new Set());
    const [favLoading, setFavLoading] = useState(false);
    const [promoSurveyOpen, setPromoSurveyOpen] = useState(false);
    const [geralSurveyOpen, setGeralSurveyOpen] = useState(false);
    const [cadastroOpen, setCadastroOpen] = useState(false);
    const storySurveyBlocked = promoSurveyOpen || geralSurveyOpen || cadastroOpen;

    useEffect(() => {
        if (!token) {
            setIsAdmin(false);
            return;
        }
        instance.get('/me')
            .then((res) => setIsAdmin(isAdminUser(res.data?.user)))
            .catch(() => setIsAdmin(false));
    }, [token]);

    const maybeTriggerPromoSurvey = useCallback(() => {
        if (sessionStorage.getItem('promo_surveys_all_done')) return;
        window.dispatchEvent(new Event('trigger_promo_survey'));
    }, []);

    useEffect(() => {
        const onPromoOpen = () => setPromoSurveyOpen(true);
        const onPromoClose = () => setPromoSurveyOpen(false);
        const onGeralOpen = () => setGeralSurveyOpen(true);
        const onGeralClose = () => setGeralSurveyOpen(false);
        const onCadastroOpen = () => setCadastroOpen(true);
        const onCadastroClose = () => {
            setCadastroOpen(false);
            if (idxRef.current >= 4) maybeTriggerPromoSurvey();
        };
        const onCadastroDone = () => {
            setCadastroOpen(false);
            if (idxRef.current >= 4) maybeTriggerPromoSurvey();
        };
        window.addEventListener('promo_survey_open', onPromoOpen);
        window.addEventListener('promo_survey_closed', onPromoClose);
        window.addEventListener('geral_survey_open', onGeralOpen);
        window.addEventListener('geral_survey_closed', onGeralClose);
        window.addEventListener('cadastro_modal_open', onCadastroOpen);
        window.addEventListener('cadastro_modal_closed', onCadastroClose);
        window.addEventListener('cadastro_completed', onCadastroDone);
        return () => {
            window.removeEventListener('promo_survey_open', onPromoOpen);
            window.removeEventListener('promo_survey_closed', onPromoClose);
            window.removeEventListener('geral_survey_open', onGeralOpen);
            window.removeEventListener('geral_survey_closed', onGeralClose);
            window.removeEventListener('cadastro_modal_open', onCadastroOpen);
            window.removeEventListener('cadastro_modal_closed', onCadastroClose);
            window.removeEventListener('cadastro_completed', onCadastroDone);
        };
    }, [maybeTriggerPromoSurvey]);

    useEffect(() => {
        if (!token) return;
        instance.get("/favorites/")
            .then(res => setFavoritedUrls(new Set(res.data.map(f => f.snapshot_image_url).filter(Boolean))))
            .catch(() => {});
    }, [token]);

    const handleFavorite = useCallback(async (e) => {
        e.stopPropagation();
        if (!token || urls.length === 0) return;
        const currentUrl = urls[idx];
        if (!currentUrl) return;

        const isFav = favoritedUrls.has(currentUrl);
        setFavLoading(true);
        try {
            if (isFav) {
                await instance.delete(`/favorites/by-url`, { params: { url: currentUrl } });
                setFavoritedUrls(prev => { const s = new Set(prev); s.delete(currentUrl); return s; });
                trackPiece("favorite_removed", { button: "unfavorite", url: currentUrl, title: titleFromImageUrl(currentUrl) });
            } else {
                await instance.post("/favorites/", {
                    snapshot_title: "Imagem do Story",
                    snapshot_image_url: currentUrl,
                    snapshot_category: "outros",
                });
                setFavoritedUrls(prev => new Set([...prev, currentUrl]));
                trackPiece("favorite_added", { button: "favorite", url: currentUrl, title: titleFromImageUrl(currentUrl) });
            }
        } catch (err) {
            console.error("Favorite error:", err);
        } finally {
            setFavLoading(false);
        }
    }, [token, urls, idx, favoritedUrls]);

    const handleWantClick = useCallback((e) => {
        e?.preventDefault?.();
        const currentUrl = urls[idx];
        if (currentUrl) {
            const brand = brandFromUrl(currentUrl);
            import('../storyCart').then(({ detectKindFromUrl }) => {
                const category = detectKindFromUrl ? detectKindFromUrl(currentUrl) : '';
                instance.post('/cart/signal', {
                    item: {
                        url: currentUrl,
                        brand: brand || '',
                        category: category || '',
                        title: titleFromImageUrl(currentUrl),
                        price: prices[currentUrl] ?? null,
                    },
                }).catch(() => {});
            }).catch(() => {});
        }
        void openTelegramVendas(null, {
            url: currentUrl,
            image_url: currentUrl,
            title: currentUrl ? titleFromImageUrl(currentUrl) : '',
            brand: currentUrl ? brandFromUrl(currentUrl) : '',
            price: currentUrl ? prices[currentUrl] ?? null : null,
            page: '/dashboard',
        });
    }, [urls, idx, prices]);

    useEffect(() => {
        let cancelled = false;
        // Extrai sinal do carrinho antes de chamar a API
        const cartItems = readStoryCart();
        const cartBrands = [...new Set(
            cartItems.map(item => item.brand || brandFromUrl(item.url || '')).filter(Boolean)
        )].slice(0, 5).join(',');
        const cartCategories = [...new Set(
            cartItems.map(item => {
                const url = item.url || '';
                if (/bolsa|bag|tote|flap/i.test(url)) return 'Bolsa';
                if (/cinto|belt/i.test(url)) return 'Cinto';
                if (/carteira|wallet/i.test(url)) return 'Carteira';
                if (/oculos|sunglass/i.test(url)) return 'Óculos';
                return '';
            }).filter(Boolean)
        )].slice(0, 5).join(',');

        const params = {};
        if (cartBrands) params.cart_brands = cartBrands;
        if (cartCategories) params.cart_categories = cartCategories;

        instance.get('/random_images', { params }).then(res => {
            if (cancelled) return;
            const list = res.data.urls || [];
            const nextPrices = res.data.prices || {};
            setIdx(0);
            setUrls(list);
            setPrices(nextPrices);
            hydrateStoryCartPrices(nextPrices);
            setFetched(true);

            // Gênio: só admin vê personalização no feed
            if (isAdmin && res.data.personalized && res.data.matchCount > 0) {
                setGenieInfo({
                    matchCount: res.data.matchCount,
                    strongMatchCount: res.data.strongMatchCount || 0,
                    injectedIndices: new Set(res.data.injectedIndices || []),
                    wish: res.data.wish || {},
                });
                setShowGenieBadge(true);
                setTimeout(() => setShowGenieBadge(false), 5000);
            } else {
                setGenieInfo(null);
                setShowGenieBadge(false);
            }

            if (isAdmin && res.data.wishExpired) {
                setWishExpired(true);
                setTimeout(() => setGenieOpen(true), 2000);
            }

            list.slice(0, 30).forEach((u, i) => enqueue(u, i < FAST_SLOTS));
            setTimeout(() => {
                list.slice(30).forEach(u => enqueue(u, false));
            }, 2000);
        }).catch(() => { if (!cancelled) setFetched(true); });
        return () => { cancelled = true; };
    }, [token, isAdmin]);

    const show = useCallback((url) => {
        setVisible(true);
        if (imgRef.current) {
            imgRef.current.src = url;
            imgRef.current.style.visibility = 'visible';
        }
    }, []);

    useEffect(() => {
        if (urls.length === 0) return;
        idxRef.current = idx;
        const url = urls[idx];

        if (isReady(url)) {
            show(url);
        } else {
            setVisible(false);
            if (imgRef.current) imgRef.current.style.visibility = 'hidden';
            boost(url);

            clearInterval(pollRef.current);
            pollRef.current = setInterval(() => {
                if (isReady(url) && idxRef.current === idx) {
                    clearInterval(pollRef.current);
                    show(url);
                }
            }, 20);
        }

        for (let i = 1; i <= 8; i++) {
            boost(urls[(idx + i) % urls.length]);
        }

        return () => clearInterval(pollRef.current);
    }, [idx, urls, show]);

    const advance = useCallback((d) => {
        if (urls.length === 0) return;
        setIdx(p => (p + d + urls.length) % urls.length);
    }, [urls]);

    useEffect(() => {
        const url = urls[idx];
        if (!url) return undefined;
        trackPiece("story_view", {
            button: "view",
            url,
            title: titleFromImageUrl(url),
            price: prices[url] ?? null,
        });
        return undefined;
    }, [idx, urls, prices]);

    useEffect(() => {
        if (idx < 4) return;
        if (cadastroOpen) return;
        maybeTriggerPromoSurvey();
    }, [idx, cadastroOpen, maybeTriggerPromoSurvey]);

    useEffect(() => {
        if (idx !== 4 && idx !== 9) return;
        if (promoSurveyOpen) return;
        window.dispatchEvent(new Event('trigger_survey'));
    }, [idx, promoSurveyOpen]);

    useEffect(() => {
        if (!visible || urls.length === 0 || storySurveyBlocked) return;
        timerRef.current = setTimeout(() => advance(1), AUTO_ADVANCE_MS);
        return () => clearTimeout(timerRef.current);
    }, [visible, idx, urls, advance, storySurveyBlocked]);

    useEffect(() => {
        const afterPromo = () => {
            window.dispatchEvent(new Event('trigger_survey'));
        };
        window.addEventListener('promo_survey_closed', afterPromo);
        return () => window.removeEventListener('promo_survey_closed', afterPromo);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setShowTip(false), 10000);
        return () => clearTimeout(t);
    }, []);

    const handleClick = (e) => {
        if (storySurveyBlocked) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (e.clientX - rect.left > rect.width / 2) advance(1);
        else advance(-1);
    };

    const progress = urls.length > 0 ? ((idx + 1) / urls.length) * 100 : 0;

    if (!fetched) {
        return (
            <div className='slider-container bg-black'>
                <div className="loading">Carregando, aguarde...</div>
            </div>
        );
    }

    if (urls.length === 0) {
        return (
            <div className='slider-container bg-black'>
                <div className="loading" style={{ color: 'white' }}>Nenhuma imagem disponível.</div>
                <Link to="/home">
                    <FontAwesomeIcon icon={faArrowCircleLeft} size='2x' style={{ color: 'white', marginLeft: '0.5rem' }} />
                </Link>
            </div>
        );
    }

    // Detecta se o slide atual é uma sugestão injetada pelo Gênio
    const isInjectedSlide = genieInfo?.injectedIndices?.has(idx);
    // Transição quando passa do bloco de matches para o resto
    const justLeftGenieZone = genieInfo && idx === genieInfo.matchCount && !isInjectedSlide;

    return (
        <div className='slider-container bg-black'>
            <div>
                <div className='slider-progress progress-top' style={{ width: `${progress}%` }} />

                {/* Badge do Gênio — aparece nos primeiros 5s quando feed é personalizado */}
                {showGenieBadge && genieInfo && (
                    <div style={{
                        position: 'absolute',
                        top: '18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 30,
                        background: 'rgba(20,10,35,0.92)',
                        border: '1px solid rgba(212,175,55,0.6)',
                        borderRadius: '20px',
                        padding: '7px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#fbf0d0',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        whiteSpace: 'nowrap',
                    }}>
                        <span>✨</span>
                        <span>
                            {genieInfo.strongMatchCount > 0
                                ? `${genieInfo.strongMatchCount} peças selecionadas para você`
                                : `Seleção personalizada ativa`}
                        </span>
                    </div>
                )}

                {/* Indicador destacado nos slides com personalização */}
                {isInjectedSlide && (
                    <div style={{
                        position: 'absolute',
                        top: '24px',
                        right: '16px',
                        zIndex: 35,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '4px',
                        pointerEvents: 'none',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #181510 0%, #2b2210 100%)',
                            border: '1.5px solid #d4af37',
                            borderRadius: '24px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '800',
                            color: '#fdf3d0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.7), 0 0 10px rgba(212,175,55,0.4)',
                            letterSpacing: '0.4px',
                            textTransform: 'uppercase',
                        }}>
                            <span style={{ fontSize: '14px' }}>✨</span>
                            <span>Seleção Personalizada</span>
                        </div>
                        <div style={{
                            background: 'rgba(0, 0, 0, 0.75)',
                            border: '1px solid rgba(212, 175, 55, 0.35)',
                            borderRadius: '10px',
                            padding: '3px 8px',
                            fontSize: '10px',
                            fontWeight: '600',
                            color: '#e8dbb5',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                        }}>
                            Especial para você
                        </div>
                    </div>
                )}

                {/* Transição: saiu da zona personalizada */}
                {justLeftGenieZone && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%,-50%)',
                        zIndex: 20,
                        background: 'rgba(0,0,0,0.75)',
                        borderRadius: '16px',
                        padding: '14px 22px',
                        textAlign: 'center',
                        color: '#ccc',
                        fontSize: '13px',
                        backdropFilter: 'blur(6px)',
                        pointerEvents: 'none',
                    }}>
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>✨</div>
                        <div>Foram esses os achados para você</div>
                        <div style={{ fontSize: '11px', marginTop: '4px', color: '#888' }}>
                            Continue para ver outros produtos do dia
                        </div>
                    </div>
                )}

                <div className='mt-3 col-12 image-container'>
                    <div className='image-wrapper' onClick={handleClick}>
                        {showTip && !visible && (
                            <div className="floating-top-container" style={{ zIndex: 10 }}>
                                <div className='bg-light rounded' style={{ padding: '0.3rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <span>
                                        Aguarde alguns segundos<br />
                                        As fotos estão carregando!
                                    </span>
                                </div>
                            </div>
                        )}

                        <img
                            ref={imgRef}
                            className='slider-image'
                            alt={`Produto ${idx + 1}`}
                            draggable={false}
                            style={{ visibility: visible ? 'visible' : 'hidden' }}
                        />

                        {!visible && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'rgba(255,255,255,0.5)'
                            }}>
                                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                            </div>
                        )}
                    </div>
                        {token && urls.length > 0 && (
                            <div style={{ position: 'absolute', bottom: '20px', left: '15px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', pointerEvents: 'none' }}>
                                <div style={{ background: 'rgba(0,0,0,0.65)', color: '#fff', padding: '10px 12px', borderRadius: '12px', fontSize: '11px', maxWidth: '140px', backdropFilter: 'blur(4px)', lineHeight: '1.4', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                                    Clique no ❤️ se gostou e mostraremos mais produtos assim!
                                </div>
                                <button
                                    onClick={handleFavorite}
                                    disabled={favLoading}
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: favoritedUrls.has(urls[idx]) ? 'rgba(231, 76, 60, 0.25)' : 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(6px)',
                                        color: favoritedUrls.has(urls[idx]) ? '#e74c3c' : '#fff',
                                        fontSize: '22px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        pointerEvents: 'auto',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    <FontAwesomeIcon icon={favoritedUrls.has(urls[idx]) ? faHeart : faHeartOutline} />
                                </button>
                            </div>
                        )}
                        <div className="floating-button-container">
                            <a href="#comprar" style={{ color: 'black', textDecoration: 'none' }} onClick={handleWantClick}>
                                <div className='bg-light rounded' style={{ padding: '0.3rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <FontAwesomeIcon icon={faLink} size='1x' style={{ color: 'black', marginRight: '0.3rem' }} />
                                    <span>
                                        Gostou? Tire print <br />
                                        clique aqui para comprar
                                    </span>
                                </div>
                            </a>
                        </div>
                </div>
                <div className="story-bottom-bar">
                    <Link to="/home" className="story-back">
                        <FontAwesomeIcon icon={faArrowCircleLeft} size='2x' />
                    </Link>
                    <AddToCartButton
                        currentItem={urls[idx] ? {
                            id: urls[idx],
                            url: urls[idx],
                            title: titleFromImageUrl(urls[idx]),
                            price: prices[urls[idx]] ?? null,
                        } : null}
                    />
                    <StoryCartDock currentItem={urls[idx] ? {
                        id: urls[idx],
                        url: urls[idx],
                        title: titleFromImageUrl(urls[idx]),
                        price: prices[urls[idx]] ?? null,
                    } : null} />
                </div>
            </div>

            {isAdmin ? (
            <PromoGenieModal
                open={genieOpen}
                wishExpired={wishExpired}
                onClose={() => { setGenieOpen(false); setWishExpired(false); }}
                destination="/dashboard"
                onApplied={() => {
                    setGenieOpen(false);
                    setWishExpired(false);
                    // Recarrega o feed com o novo wish
                    navigate(0);
                }}
            />
            ) : null}
        </div>
    );
}

export default Dashboard;
